// Re-KYC (spec §5.2): status card with risk period, three flows (A no change,
// B address only, C other), the "Also confirm" card, reminders and the debit
// restriction grace. Uses the bank clock; OTP steps through the auth chain.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { rekyc, rekycNoChange, rekycAddressChange, lookupPin, fatca } from '@/api/bankServices';
import { runBankClock } from '@/mock/phase4';
import { usePaymentAuth } from '@/pages/Pay/components/usePaymentAuth';
import PaymentAuthSheets from '@/pages/Pay/components/PaymentAuthSheets';
import '../phase4.css';

const RISK_PERIOD: Record<string, string> = { LOW: '10 years', MEDIUM: '8 years', HIGH: '2 years' };

const Rekyc = () => {
  const { flow } = useParams<{ flow?: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<Awaited<ReturnType<typeof rekyc>> | null>(null);

  const load = () => {
    runBankClock();
    rekyc().then(setState).catch(() => setState(null));
  };
  useEffect(load, []);

  const flows = [
    { id: 'no-change', title: 'No change in my details', sub: 'Confirm everything is the same' },
    { id: 'address', title: 'Only my address changed', sub: 'New address with a PIN lookup' },
    { id: 'other', title: 'Other details changed', sub: 'Video KYC, branch or BC' },
  ];

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => (flow ? navigate('/profile/bank/rekyc') : navigate('/profile/bank'))}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Re-KYC</h2>
      </div>

      {state && (
        <div className="nova-p4-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="nova-p4-row-title">
                {state.status === 'UP_TO_DATE' ? 'Up to date' : state.status === 'DUE' ? `Due on ${new Date(state.dueAt).toLocaleDateString('en-IN')}` : `Overdue since ${new Date(state.dueAt).toLocaleDateString('en-IN')}`}
              </div>
              <div className="nova-p4-row-sub">
                {state.risk} risk · every {RISK_PERIOD[state.risk]} · last updated {new Date(state.lastUpdatedAt).toLocaleDateString('en-IN')}
              </div>
            </div>
            <span className={`nova-p4-status ${state.status === 'UP_TO_DATE' ? 'ok' : 'warn'}`}>{state.status.replace(/_/g, ' ')}</span>
          </div>
          {state.restrictsDebits && <div className="nova-p4-banner red" style={{ marginTop: 10 }}>Re-KYC is overdue. Debits are paused until you complete Re-KYC; credits still come in.</div>}
          {state.status === 'OVERDUE' && !state.restrictsDebits && state.usableUntil && (
            <div className="nova-p4-banner amber" style={{ marginTop: 10 }}>Complete Re-KYC by {new Date(state.usableUntil).toLocaleDateString('en-IN')} or debits will be paused.</div>
          )}
          {state.addressChange?.status === 'UNDER_VERIFICATION' && (
            <div className="nova-p4-banner blue" style={{ marginTop: 10 }}>Your new address is under verification.</div>
          )}
        </div>
      )}

      {!flow && flows.map((f) => (
        <button key={f.id} type="button" className="nova-p4-row" onClick={() => navigate(`/profile/bank/rekyc/${f.id}`)}>
          <span className="nova-p4-row-text">
            <span className="nova-p4-row-title">{f.title}</span>
            <span className="nova-p4-row-sub">{f.sub}</span>
          </span>
        </button>
      ))}

      {flow === 'no-change' && <NoChange onDone={load} />}
      {flow === 'address' && <AddressChange onDone={load} />}
      {flow === 'other' && <OtherChanges />}
    </div>
  );
};

// ---------- "Also confirm" card (A and B) ----------

const AlsoConfirm = ({ confirmed, onChange }: { confirmed: boolean; onChange: (v: boolean) => void }) => {
  const [fatcaState, setFatcaState] = useState<Awaited<ReturnType<typeof fatca>> | null>(null);
  useEffect(() => { fatca().then(setFatcaState).catch(() => undefined); }, []);
  return (
    <div className="nova-p4-card">
      <h3>Also confirm</h3>
      <div className="nova-p4-moneyline muted"><span>Tax residency</span><span>{fatcaState?.foreignTaxResident ? 'Outside India' : 'India'}</span></div>
      <div className="nova-p4-moneyline muted"><span>US person</span><span>{fatcaState?.usPerson ? 'Yes' : 'No'}</span></div>
      <label style={{ display: 'flex', gap: 8, marginTop: 10, fontSize: '0.88rem' }}>
        <input type="checkbox" checked={confirmed} onChange={(e) => onChange(e.target.checked)} />
        My FATCA/CRS details, occupation and income above are unchanged.
      </label>
    </div>
  );
};

// ---------- A. No change ----------

const NoChange = ({ onDone }: { onDone: () => void }) => {
  const [confirmKyc, setConfirmKyc] = useState(false);
  const [confirmAlso, setConfirmAlso] = useState(false);
  const auth = usePaymentAuth(async () => {
    try {
      await rekycNoChange();
      toast.success('Re-KYC completed');
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not complete Re-KYC.');
    }
  });
  const submit = async () => {
    const e = await auth.start('NOVABANK', 'Re-KYC — no change');
    if (e) toast.error(e);
  };
  return (
    <>
      <div className="nova-p4-card">
        <h3>Your details</h3>
        <div className="nova-p4-moneyline muted"><span>Name</span><span>Account holder</span></div>
        <div className="nova-p4-moneyline muted"><span>PAN</span><span>ABXXXXX34F</span></div>
        <div className="nova-p4-moneyline muted"><span>Address</span><span>Communication address on record</span></div>
        <label style={{ display: 'flex', gap: 8, marginTop: 12, fontSize: '0.88rem' }}>
          <input type="checkbox" checked={confirmKyc} onChange={(e) => setConfirmKyc(e.target.checked)} />
          I confirm there is no change in my KYC information.
        </label>
      </div>
      <AlsoConfirm confirmed={confirmAlso} onChange={setConfirmAlso} />
      <button type="button" className="nova-p4-primary" disabled={!confirmKyc || !confirmAlso} onClick={() => void submit()}>Confirm (OTP)</button>
      <PaymentAuthSheets authState={auth.authState} approvalLine="Re-KYC" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </>
  );
};

// ---------- B. Address only ----------

const AddressChange = ({ onDone }: { onDone: () => void }) => {
  const [house, setHouse] = useState('');
  const [street, setStreet] = useState('');
  const [area, setArea] = useState('');
  const [pin, setPin] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [declare, setDeclare] = useState(false);
  const [confirmAlso, setConfirmAlso] = useState(false);

  useEffect(() => {
    if (/^\d{6}$/.test(pin)) {
      const hit = lookupPin(pin);
      if (hit) { setCity(hit.city); setState(hit.state); }
    }
  }, [pin]);

  const auth = usePaymentAuth(async () => {
    try {
      await rekycAddressChange({ house, street, area, pin, city, state });
      toast.success('Under verification');
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not submit.');
    }
  });
  const submit = async () => {
    const e = await auth.start('NOVABANK', 'Re-KYC — address change');
    if (e) toast.error(e);
  };

  return (
    <>
      <div className="nova-p4-card">
        <h3>New address</h3>
        <div className="nova-p4-field"><label htmlFor="a_house">House / flat</label><input id="a_house" className="form-control" value={house} onChange={(e) => setHouse(e.target.value)} /></div>
        <div className="nova-p4-field"><label htmlFor="a_street">Street</label><input id="a_street" className="form-control" value={street} onChange={(e) => setStreet(e.target.value)} /></div>
        <div className="nova-p4-field"><label htmlFor="a_area">Area</label><input id="a_area" className="form-control" value={area} onChange={(e) => setArea(e.target.value)} /></div>
        <div className="nova-p4-field">
          <label htmlFor="a_pin">PIN code</label>
          <input id="a_pin" className="form-control" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
          {city && <div className="hint">{city}, {state}</div>}
        </div>
        <div className="nova-p4-field"><label htmlFor="a_proof">Proof upload (optional)</label><input id="a_proof" className="form-control" type="file" /></div>
        <label style={{ display: 'flex', gap: 8, fontSize: '0.88rem' }}>
          <input type="checkbox" checked={declare} onChange={(e) => setDeclare(e.target.checked)} />
          I declare that the new address above is correct and where I live.
        </label>
      </div>
      <AlsoConfirm confirmed={confirmAlso} onChange={setConfirmAlso} />
      <button type="button" className="nova-p4-primary" disabled={!declare || !confirmAlso || !house || !pin || !city} onClick={() => void submit()}>Submit (OTP)</button>
      <PaymentAuthSheets authState={auth.authState} approvalLine="Re-KYC address" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </>
  );
};

// ---------- C. Other ----------

const OtherChanges = () => (
  <div className="nova-p4-card">
    <h3>Other changes</h3>
    <div className="nova-p4-banner blue">Update via Video KYC is available when the VKYC switch is on.</div>
    <button type="button" className="nova-p4-row" style={{ marginTop: 8 }}>
      <span className="nova-p4-row-text"><span className="nova-p4-row-title">Update via Video KYC</span></span>
    </button>
    <button type="button" className="nova-p4-row">
      <span className="nova-p4-row-text"><span className="nova-p4-row-title">Visit branch</span><span className="nova-p4-row-sub">Find the nearest branch</span></span>
    </button>
    <button type="button" className="nova-p4-row">
      <span className="nova-p4-row-text"><span className="nova-p4-row-title">Visit a NovaBank Business Correspondent</span><span className="nova-p4-row-sub">Find the nearest BC</span></span>
    </button>
  </div>
);

export default Rekyc;
